import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Eye } from 'lucide-react';

interface Producto {
  id: string;
  id_catalogo: string;
  nombre: string;
  marca: string;
  categoria: string;
  descripcion?: string;
  imagen?: string;
  sitiosTrackeados: number;
  ultimoPrecio?: number;
  fechaActualizacion: string;
}

const Productos: React.FC = () => {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMarca, setSelectedMarca] = useState<string>('todas');
  const [selectedCategoria, setSelectedCategoria] = useState<string>('todas');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');

  // Mock data for initial development
  useEffect(() => {
    // TODO: Fetch products from backend
    setProductos([
      { 
        id: '1', 
        id_catalogo: 'PROD001',
        nombre: 'Samsung Galaxy S23', 
        marca: 'Samsung', 
        categoria: 'Smartphones',
        descripcion: 'Smartphone flagship con cámara de 200MP',
        sitiosTrackeados: 8,
        ultimoPrecio: 899999,
        fechaActualizacion: '2024-01-15'
      },
      { 
        id: '2', 
        id_catalogo: 'PROD002',
        nombre: 'Nike Air Max 90', 
        marca: 'Nike', 
        categoria: 'Calzado',
        descripcion: 'Zapatillas deportivas clásicas',
        sitiosTrackeados: 5,
        ultimoPrecio: 129999,
        fechaActualizacion: '2024-01-14'
      },
      { 
        id: '3', 
        id_catalogo: 'PROD003',
        nombre: 'iPad Pro 12.9', 
        marca: 'Apple', 
        categoria: 'Tablets',
        descripcion: 'Tablet profesional con chip M2',
        sitiosTrackeados: 6,
        ultimoPrecio: 1299999,
        fechaActualizacion: '2024-01-13'
      },
    ]);
  }, []);

  const marcas = ['todas', ...Array.from(new Set(productos.map(p => p.marca)))];
  const categorias = ['todas', ...Array.from(new Set(productos.map(p => p.categoria)))];

  const filteredProductos = productos.filter(producto => {
    const matchesSearch = 
      producto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      producto.id_catalogo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      producto.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMarca = selectedMarca === 'todas' || producto.marca === selectedMarca;
    const matchesCategoria = selectedCategoria === 'todas' || producto.categoria === selectedCategoria;
    return matchesSearch && matchesMarca && matchesCategoria;
  });

  const formatPrice = (price?: number) => {
    if (!price) return '-';
    return `$${price.toLocaleString()}`;
  };

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Gestión de Productos</h2>
        <div className="flex gap-3">
          <div className="btn-group">
            <button 
              onClick={() => setViewMode('table')}
              className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-default'}`}
            >
              Tabla
            </button>
            <button 
              onClick={() => setViewMode('grid')}
              className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-default'}`}
            >
              Cuadrícula
            </button>
          </div>
          <button className="btn btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Nuevo Producto
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="flex-1 min-w-[300px] relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar productos por nombre o ID..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          value={selectedMarca}
          onChange={(e) => setSelectedMarca(e.target.value)}
        >
          <option value="todas">Todas las marcas</option>
          {marcas.slice(1).map(marca => (
            <option key={marca} value={marca}>{marca}</option>
          ))}
        </select>
        <select
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          value={selectedCategoria}
          onChange={(e) => setSelectedCategoria(e.target.value)}
        >
          <option value="todas">Todas las categorías</option>
          {categorias.slice(1).map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Products display */}
      {viewMode === 'table' ? (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID Catálogo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Producto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Marca
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoría
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sitios
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Último Precio
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProductos.map((producto) => (
                <tr key={producto.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {producto.id_catalogo}
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{producto.nombre}</div>
                      {producto.descripcion && (
                        <div className="text-sm text-gray-500">{producto.descripcion}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {producto.marca}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {producto.categoria}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {producto.sitiosTrackeados}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatPrice(producto.ultimoPrecio)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-blue-600 hover:text-blue-900 mr-3">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="text-indigo-600 hover:text-indigo-900 mr-3">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button className="text-red-600 hover:text-red-900">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProductos.map((producto) => (
            <div key={producto.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="aspect-square bg-gray-100 flex items-center justify-center">
                <div className="text-gray-400 text-center p-4">
                  <div className="text-xs">{producto.id_catalogo}</div>
                  <div className="text-sm mt-1">Sin imagen</div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-sm mb-1">{producto.nombre}</h3>
                <p className="text-xs text-gray-500 mb-2">{producto.marca} • {producto.categoria}</p>
                {producto.descripcion && (
                  <p className="text-xs text-gray-600 mb-3 line-clamp-2">{producto.descripcion}</p>
                )}
                <div className="flex justify-between items-center mb-3">
                  <span className="text-lg font-bold">{formatPrice(producto.ultimoPrecio)}</span>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    {producto.sitiosTrackeados} sitios
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <button className="flex-1 btn btn-sm btn-default">Ver</button>
                  <button className="flex-1 btn btn-sm btn-primary">Editar</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredProductos.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No se encontraron productos
        </div>
      )}
    </div>
  );
};

export default Productos;